import * as Yup from 'yup';

import { FormField } from '@components';
import { ApiEndpoint, FormType, InputMod, InputType, StorageUnits, Translation } from '@enums';
import i18n from '@translations';
import type { EndpointType, FormConfig, FormConfigMapping, OptionItem } from '@types';
import { convertEnumToObjectArray } from '@utils';

interface GetAppConfigurationFormConfigParams {
  endpoint?: EndpointType;
  schema?: Yup.AnySchema;
  loading?: boolean;
  httpPortOption?: OptionItem[];
  httpsPortOption?: OptionItem[];
  httpSelectedInput?: OptionItem[];
  httpsSelectedInput?: OptionItem[];
  maxBodySizeMaxValue?: number;
  maxPostArgumentMaxValue?: number;
  maxUrlArgumentMaxValue?: number;
}

export const getAppConfigurationFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  schema,
  loading,
  httpPortOption = [],
  httpsPortOption = [],
  httpSelectedInput = [],
  httpsSelectedInput = [],
  maxBodySizeMaxValue,
  maxPostArgumentMaxValue,
  maxUrlArgumentMaxValue,
}: GetAppConfigurationFormConfigParams): FormConfig => {
  const APP_CONFIGURATION_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnApplicationConfiguration,
      endpoint: ApiEndpoint.DEFAULT,
      inputList: [
        {
          name: 'name',
          label: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.applicationName`),
          description: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.updateApplicationName`),
          placeholder: i18n.t(`${Translation.Configuration}:forms.appConfiguration.applicationNamePlaceHolder`),
          inputMod: InputMod.Filled,
          customizeColor: true,
          withoutLabel: true,
          textInputClassName: 'bg-white',
          loading,
        },
        {
          name: 'http_port',
          label: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.httpPort`),
          description: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.httpPortDescription`),
          placeholder: i18n.t(`${Translation.Configuration}:forms.appConfiguration.httpPlaceHolder`),
          inputType: InputType.MultipleSelectInput,
          inputMod: InputMod.Filled,
          withoutLabel: true,
          withoutChip: true,
          showChip: true,
          textInputClassName: 'bg-white',
          options: httpPortOption,
          selectedInput: httpSelectedInput,
          loading,
        },
        {
          name: 'https_port',
          label: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.httpsPort`),
          description: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.httpsPortDescription`),
          placeholder: i18n.t(`${Translation.Configuration}:forms.appConfiguration.httpsPlaceHolder`),
          inputType: InputType.MultipleSelectInput,
          inputMod: InputMod.Filled,
          withoutLabel: true,
          withoutChip: true,
          showChip: true,
          textInputClassName: 'bg-white',
          options: httpsPortOption,
          selectedInput: httpsSelectedInput,
          loading,
        },
        {
          name: 'max_body_size',
          type: 'number',
          min: 1,
          max: maxBodySizeMaxValue,
          label: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.maxBodySize`),
          description: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.maxBodySizeDescription`),
          inputMod: InputMod.Filled,
          customizeColor: true,
          withoutLabel: true,
          textInputClassName: 'bg-white',
          loading,
          adornment: (
            <FormField
              label={i18n.t(`${Translation.Configuration}:forms.appConfiguration.unit`)}
              name="max_body_size_unit"
              inputType={InputType.Select}
              withoutLabel
              inputMod={InputMod.Filled}
              centerItem
              textInputClassName="bg-transparent ring-0 focus:ring-0"
              labelClassName="top-0.5"
              customWidth="w-20"
              className="flex border-l"
              loading={false}
              placeholder={i18n.t(`${Translation.Configuration}:forms.appConfiguration.selectYourUnit`)}
              options={convertEnumToObjectArray(StorageUnits)}
            />
          ),
        },
        {
          name: 'max_post_argument',
          type: 'number',
          min: 1,
          max: maxPostArgumentMaxValue,
          label: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.maxPostArguments`),
          description: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.maxPostArgumentsDescription`),
          inputMod: InputMod.Filled,
          customizeColor: true,
          withoutLabel: true,
          textInputClassName: 'bg-white',
          loading,
        },
        {
          name: 'max_url_argument',
          type: 'number',
          min: 1,
          max: maxUrlArgumentMaxValue,
          label: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.maxUrlArguments`),
          description: i18n.t(`${Translation.Configuration}:tabs.appConfiguration.maxUrlArgumentsDescription`),
          inputMod: InputMod.Filled,
          fullWidth: true,
          customizeColor: true,
          withoutLabel: true,
          textInputClassName: 'bg-white',
          loading,
        },
        {
          name: 'enable_websocket',
          label: i18n.t(`${Translation.Configuration}:forms.appConfiguration.webSocket`),
          description: i18n.t(`${Translation.Configuration}:forms.appConfiguration.webSocketDescription`),
          inputType: InputType.Switch,
          fullWidth: true,
          customizeColor: true,
          withoutLabel: true,
          centerItem: true,
          textInputClassName: 'bg-white',
          loading,
        },
      ],
      validationSchema: schema,
    },
  };

  return (APP_CONFIGURATION_FORM[endpoint] ?? APP_CONFIGURATION_FORM[ApiEndpoint.DEFAULT]) as FormConfig;
};
